import { getTranslation } from '@mahawi/eshop-common/src/translation';
import {
  type IAdminCategory,
  type ILanguageType,
  type ITranslation,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Button, Col, Divider, Form, Input, Row } from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import slugify from 'slugify';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import {
  slugLive,
  slugsUpdate,
  slugUpdate,
} from '../../reducers/category/reducer';
import FormSubmitFooter from '../form-submit-footer';

interface IForm {
  slug: string;
}

const getDirectTranslation = (
  translations: ITranslation[] | undefined,
  lang?: ILanguageType,
): string | null =>
  translations?.find(({ code }: ITranslation): boolean => code === lang?.code)
    ?.value ?? null;

function SlugForm(): React.ReactElement {
  const dispatch = useAppDispatch();
  const languageType = useSelector((s: RootState) => s.Language.languageType);
  const languages = useSelector((s: RootState) => s.Language.languages);
  const category = useSelector((s: RootState) => s.Category.category);
  const categoryInProcess = useSelector((s: RootState) => s.Category.inProcess);
  const categoryUpdatedAt = useSelector((s: RootState) => s.Category.updatedAt);

  const [form] = Form.useForm<IForm>();

  const slugValue = useMemo(
    () => getDirectTranslation(category?.slugs, languageType) ?? '',
    [category?.slugs, languageType, categoryUpdatedAt],
  );

  useEffect((): void => {
    form.setFieldsValue({ slug: slugValue });
  }, [slugValue, form]);

  const onFinish = useCallback(
    ({ slug }: IForm, cat: IAdminCategory | undefined): void => {
      if (cat && languageType) {
        dispatch(slugUpdate(cat.uuid, slug, languageType.code));
      }
    },
    [languageType?.code, dispatch],
  );

  const generateSlug = useCallback((): void => {
    if (!category) {
      return;
    }

    const batch: { slug: string; languageTypeCode: string }[] = [];

    languages?.forEach((language: ILanguageType): void => {
      const existingSlug: string | null = getDirectTranslation(
        category?.slugs,
        language,
      );

      if (existingSlug && existingSlug.length >= 2) {
        return;
      }

      const name: string | null = getTranslation(category?.names, language);

      if (!name) {
        return;
      }

      const generated: string = slugify(name, { lower: true, strict: true });

      dispatch(slugLive(generated, language.code));
      batch.push({ slug: generated, languageTypeCode: language.code });

      if (language.code === languageType?.code) {
        form.setFieldValue('slug', generated);
      }
    });

    if (batch.length > 0) {
      dispatch(slugsUpdate(category.uuid, batch));
    }
  }, [
    category,
    category?.names,
    category?.slugs,
    languages,
    languageType?.code,
    dispatch,
    form,
  ]);

  return (
    <Form
      form={form}
      onFinish={(values: IForm): void | null | undefined =>
        category && onFinish(values, category)
      }
      layout="vertical"
    >
      <Row>
        <Col span={3}>
          <FormSubmitFooter
            inProcess={categoryInProcess}
            updatedAt={categoryUpdatedAt}
          />
        </Col>

        <Col span={3}>
          <Button onClick={generateSlug} disabled={categoryInProcess}>
            Generate from name
          </Button>
        </Col>
      </Row>

      <Divider />

      <Row>
        <Col span={24}>
          <Form.Item
            name="slug"
            rules={[
              { required: true },
              { type: 'string', min: 2 },
              {
                pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                message: 'Only lowercase letters, numbers and hyphens',
              },
            ]}
          >
            <Input
              placeholder="category-slug"
              disabled={categoryInProcess}
              onBlur={(e): void => {
                if (!languageType) {
                  return;
                }

                dispatch(slugLive(e.target.value.trim(), languageType.code));
              }}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}

function Slug(): React.ReactElement {
  return <SlugForm />;
}

export default Slug;
